<template>
<div
  v-if="!$vuetify.breakpoint.mobile || showOnMobile"
  :key="mapKey"
  :style="`position: relative; margin: 0 auto; width: 100%; height: 100%; max-width: ${maxWidth}px; max-height: ${maxHeight}px; display: flex; justify-content: center; align-items: center;`"
>
  <v-menu>
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        x-small
        text
        fab
        style="position: absolute; top: 3px; right: 0px; z-index: 1;"
        v-bind="attrs"
        v-on="on"
      >
        <v-icon color="grey">mdi-dots-vertical</v-icon>
      </v-btn>
    </template>
    <v-list>
      <v-list-item @click="downloadImage()">
        <v-list-item-icon><v-icon>mdi-download</v-icon></v-list-item-icon>
        <v-list-item-title>
          Lataa kuvana
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
  <v-stage
    style="border: 1px dashed black; cursor: crosshair;"
    @click="handleStageClick"
    ref="stage"
    :config="{
      width,
      height,
    }"
  >
    <v-layer>
      <v-image :config="{ image: image, height, width }">
      </v-image>
      <v-circle
        @mouseover="handleMouseOver"
        @mouseleave="handleMouseLeave"
        @click="handleObjectClick(obj)"
        @tap="handleObjectClick(obj)"
        v-for="(obj, i) in objects.filter(p => p.shape === 'circle')"
        :key="i + 'circle'"
        :config="circleConfig(obj)"
      ></v-circle>
      <v-rect
        @mouseover="handleMouseOver"
        @mouseleave="handleMouseLeave"
        @click="handleObjectClick(obj)"
        @tap="handleObjectClick(obj)"
        v-for="(obj, i) in objects.filter(p => p.shape === 'square')"
        :key="i + 'rect'"
        :config="rectConfig(obj)"
      />
      <!-- MOUSE -->
      <v-circle :config="{ x: mouse.x, y: mouse.y, radius: 2, fill: 'blue' }" />
    </v-layer>
  </v-stage>
</div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  props: {
    image_url: {
      type: String,
      required: true
    },
    maxWidth: {
      type: Number,
      required: false,
      default: 300
    },
    maxHeight: {
      type: Number,
      required: false,
      default: 300
    },
    offsets: {
      type: Object, // { top, bottom, left, right  }
      required: false,
      default: () => {
        return {
          top: 0,
          bottom: 0,
          left: 0,
          right: 0
        }
      }
    },
    dimensions: {
      type: Array, // { height, width }
      required: false
    },
    /*
      [
        {
          x: 0.453,
          y: 0.565,
          shape: 'circle',
          color: ''
        }
      ]
    */
    objects: {
      type: Array,
      required: false,
      default: () => []
    },
    showOnMobile: {
      type: Boolean,
      default: false
    },
  },
  data: () => ({
    image: null,
    height: null,
    width: null,
    img_height: null,
    img_width: null,
    mapKey: 0,
    x: 0,
    y: 0,
  }),
  created() {
    const image = new window.Image();
    image.crossOrigin = "anonymous";
    image.src = this.image_url;

    image.onload = () => {
      this.image = image;
      this.img_height = image.height;
      this.img_width = image.width;

      this.$nextTick(() => {
        this.handleDimensions();
      });
    }
  },
  computed: {
    ...mapGetters('analysis', [
      'points',
      'mouse_position'
    ]),
    mouse() {
      return this.calculateExternalPosition({
        x: this.mouse_position.x * this.width,
        y: this.mouse_position.y * this.height
      })
    }
  },
  methods: {
    handleMouseOver() {
      this.$refs.stage.getNode().container().style.cursor = 'pointer'
    },
    handleMouseLeave() {
      this.$refs.stage.getNode().container().style.cursor = 'crosshair'
    },
    downloadImage() {
      const dataURL = this.$refs.stage.getNode().toDataURL({
        mimeType: "image/jpeg",
        pixelRatio: 2
      });

      const link = document.createElement("a");
      link.href = dataURL;
      link.download = `lyontikartta-${Date.now()}.png`; // File name for download
      document.body.appendChild(link); // Append to the document
      link.click(); // Trigger the download
      document.body.removeChild(link); // Cleanup
    },
    handleObjectClick(obj) {
      const eventId = obj.eventId;

      if (eventId) {
        this.$emit('eventClicked', eventId);
      }
    },
    handleStageClick(e) {
      // Prevent click event from firing twice on mobile
      if (e.type === 'touchend') {
        e.evt.preventDefault();
      }

      const x = e.evt.offsetX || e.evt.touches?.[0]?.clientX - e.target.getStage().container().getBoundingClientRect().left;
      const y = e.evt.offsetY || e.evt.touches?.[0]?.clientY - e.target.getStage().container().getBoundingClientRect().top;

      this.$emit('click', { x: x / this.width, y: y / this.height });
    },
    handleDimensions() {
      const { img_height, img_width } = this;
      const height = this.maxHeight;
      const width = this.maxWidth;
      const aspect_ratio = img_height / img_width;

      if (img_height > img_width) {
        this.height = height;
        this.width = height / aspect_ratio;
      } else {
        this.width = width;
        this.height = width * aspect_ratio;
      }

      this.$nextTick(() => {
        this.mapKey++
      });
    },
    handleExternalClick({ point }) {
      if(!this.offsets) return

      const [ x, y ] = point
      const trans_x = ( this.offsets.right - this.offsets.left ) * x
      const trans_y = ( this.offsets.bottom - this.offsets.top ) * y

      this.x = (trans_x + this.offsets.left) * this.width
      this.y = (trans_y + this.offsets.top) * this.height

      this.$emit('click', { x: this.x / this.width, y: this.y / this.height })

      this.$nextTick(() => {
        this.mapKey++
      });
    },
    calculateExternalPosition({ x, y }) {
      if(!this.offsets) return

      let trans_x = ( this.offsets.right - this.offsets.left ) * x / this.width
      let trans_y = ( this.offsets.bottom - this.offsets.top ) * y / this.height
      let tran_x = (trans_x + this.offsets.left) * this.width
      let tran_y = (trans_y + this.offsets.top) * this.height

      return { x: tran_x, y: tran_y }
    },
    rectConfig(obj) {
      const size = (obj.size || 5) * 1.75
      const stroke = (obj.strokeWidth || 1)
      return {
        strokeWidth: stroke,
        stroke: obj.strokeColor,
        opacity: obj.opacity,
        x: obj.x * this.width - size / 2,
        y: obj.y * this.height - size / 2,
        height: size,
        width: size,
        fill: obj.color
      }
    },
    circleConfig(obj) {
      return {
        strokeWidth: obj.strokeWidth || 1,
        stroke: obj.strokeColor || 'black',
        opacity: obj.opacity || 1,
        x: obj.x * this.width,
        y: obj.y * this.height,
        radius: obj.size || 5,
        fill: obj.color || 'grey'
      }
    }
  },
  watch: {
    'points': {
      deep: true,
      handler(newVal) {
        const point = newVal[newVal.length - 1]
        this.handleExternalClick(point)
      }
    }
  }
}
</script>

<style scoped>
.image-container {
  width: 100%;
  max-width: var(--max-width);
  height: 100%;
  max-height: var(--max-height);
  display: flex;
  justify-content: center;
  align-items: center;
  object-fit: contain; /* Scales image while maintaining aspect ratio */
  overflow: hidden; /* Prevents image overflow */
}

img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain; /* Scales image while maintaining aspect ratio */
}
</style>